/**
 * Copyright (c) 2010-2024 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { Component, ElementRef, EventEmitter, HostListener, Input, Output, ViewChild } from '@angular/core';
import { LoggerService } from '../../../modules/shared/services/logger.service';
import { DashGisUtil } from '../../../modules/shared/dash-gis-util';

@Component({
    selector: 'dash-gis-file-upload',
    templateUrl: './file-upload.component.html',
    styleUrls: ['./file-upload.component.scss'],
})
export class FileUploadComponent {
    @Input() public maxFiles = 1;
    @Input() public accept = 'image/*;*.pdf';
    @Input() public disabled = false;

    @Output() public uploadSelected = new EventEmitter<File[]>();

    @ViewChild('dropTarget', { static: true })
    dropTarget: ElementRef<HTMLDivElement>;

    isDraggingOver = false;

    public constructor(private logger: LoggerService) {}

    @HostListener('dragenter', ['$event'])
    @HostListener('dragover', ['$event'])
    handleDrag(evt) {
        if (this.disabled) {
            return;
        }
        this.isDraggingOver = true;
        evt.stopPropagation();
        evt.preventDefault();
    }

    @HostListener('dragleave', ['$event'])
    handleDragLeave(evt: DragEvent) {
        if (this.disabled) {
            return;
        }
        const dropTarget = this.dropTarget.nativeElement;
        const relatedTarget = evt.relatedTarget as HTMLElement;
        if (evt.target === this.dropTarget.nativeElement && !DashGisUtil.isAncestor(dropTarget, relatedTarget)) {
            this.isDraggingOver = false;
        }

        evt.stopPropagation();
        evt.preventDefault();
    }

    @HostListener('drop', ['$event'])
    fileDrop(evt: DragEvent) {
        if (this.disabled) {
            return;
        }
        this.isDraggingOver = false;
        const filesToSend = this.collectFiles(evt.dataTransfer.files);
        this.uploadSelected.emit(filesToSend);
        evt.preventDefault();
    }

    inputFieldChange(evt) {
        const fileInputControl = evt.target as HTMLInputElement;
        if (fileInputControl.files.length > 0) {
            const filesToSend = this.collectFiles(fileInputControl.files);
            this.uploadSelected.emit(filesToSend);

            // reset the input control value so if we reset the form and add the same file again, it registers
            // as an input change event
            // Note: consumer should verify the same file isn't added twice
            fileInputControl.value = '';
        }
    }

    private collectFiles(files: FileList): File[] {
        const filesToSend: File[] = [];
        for (let i = 0; i < Math.min(files.length, this.maxFiles); i++) {
            filesToSend[i] = files[i];
        }
        return filesToSend;
    }
}
